import React, { useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useLocation } from 'react-router-dom';
import Pagination from '../components/common/Pagination';
import ByClientMonthSongsTable from '../components/ByClientMonthSongsTable';
import Breadcrumb from '../components/Breadcrumb';
import { useSelector } from 'react-redux';
import { authSelector } from '../features/auth/slice';

function ByClientMonthSongsPage() {
  const { me } = useSelector(authSelector.selectAuthState);
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const clientId = location.state?.clientId ?? params.get('clientId');
  const albumId = params.get('albumId');
  const calculateMonth = params.get('calculateMonth');

  const [loading, setLoading] = useState(false);
  const [byClientMonthSongs, setByClientMonthSongs] = useState([]);
  const [month, setMonth] = useState('');
  const [clientName, setClientName] = useState('권리사');
  const [albumName, setAlbumName] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 15;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/ByClient/byClientMonthSongs`,
        {
          params: {
            clientId,
            albumId,
            calculateMonth,
            page: currentPage,
            limit: pageSize,
          },
        }
      );

      setByClientMonthSongs(response.data.items);
      setTotalPages(response.data.totalPages);
      setCurrentPage(response.data.currentPage);
      setTotalCount(response.data.totalCount);
    } catch (error) {
      console.error('서버 접속 에러:', error);
      setByClientMonthSongs([]);
      setTotalPages(1);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, albumId, calculateMonth, currentPage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const monthList = useMemo(() => {
    return Array.from(
      new Set(byClientMonthSongs.map((song) => song.calculateMonth.slice(0, 7)))
    )
      .sort()
      .reverse();
  }, [byClientMonthSongs]);

  useEffect(() => {
    if (monthList.length > 0 && !month) {
      setMonth(monthList[0]);
    }
  }, [monthList, month]);

  useEffect(() => {
    const found = byClientMonthSongs.find(
      (a) => a.calculateMonth.slice(0, 7) === month
    );
    if (found) {
      setClientName(found.album?.albumClients?.client?.name || '권리사');
      setAlbumName(found.album?.nameKr || '');
    }
  }, [byClientMonthSongs, month]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const processedSongs = useMemo(() => {
    return byClientMonthSongs
      .filter((song) => song.calculateMonth.slice(0, 7) === month)
      .map((song) => ({
        ...song,
        finalAmount: song.clientAmount,
      }))
      .sort((a, b) => b.finalAmount - a.finalAmount);
  }, [byClientMonthSongs, month]);

  const showTitle = month !== '';

  const formatMonth = (monthStr) => {
    if (!monthStr) return '';
    const [year, mon] = monthStr.split('-');
    const monthText = `${year}년 ${parseInt(mon, 10)}월`;

    if (me?.adminYn === 'Y') {
      return `${monthText} ${clientName} '${albumName}' 곡별 정산`;
    } else {
      return `${monthText} '${albumName}' 곡별 정산`;
    }
  };

  const isAdmin = me?.adminYn === 'Y';

  const breadcrumbItems = [
    { label: isAdmin ? '당월권리사별' : '월별리포트', to: '/ByClientLatestMonth' },
    { label: '앨범별 정산', to: `/ByClientMonthAlbums?clientId=${clientId ?? ''}&calculateMonth=${calculateMonth}` },
    { label: '곡별 정산' },
  ];

  return (
    <>
        <Breadcrumb items={breadcrumbItems} />
        {showTitle && (
          <div className="flex justify-center mb-8">
            <div className="text-xl sm:text-2xl font-bold text-center">
              {formatMonth(month)}
            </div>
          </div>
        )}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-sky-500 mr-3"></div>
            <div className="text-sm text-slate-500">데이터 불러오는 중...</div>
          </div>
        ) : (
          <>
            <ByClientMonthSongsTable
              data={processedSongs}
              totalCount={totalCount}
              currentPage={currentPage}
              pageSize={pageSize}
              month={month}
              clientId={clientId}
            />

            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </>
        )}
    </>
  );
}

export default ByClientMonthSongsPage;
